import React from 'react';
import Head from 'next/head';
import { NextRouter } from 'next/router';
import { useRouter } from 'next/compat/router';

import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useRequestHost } from 'contexts/request-host';
import PublicEnv from 'shared/utils/public-env';

const getDispensaryPathEquivalent = (path: string): string => {
  const splitPath = path.split('/');
  splitPath.splice(1, 1, `dispensary`);

  return splitPath.join('/');
};

type SanitizePathForSeo = {
  sanitizedPath: string;
};

function sanitizePathForSeo({ asPath }: Partial<NextRouter>): SanitizePathForSeo {
  let sanitizedPath = asPath ?? '';

  /*
    We need to sanitized the path and remove the query params.
    For seo purposes they are unecessarily creating duplicate indices.
  */
  if (asPath) {
    const [path, queryString] = asPath.split('?');
    sanitizedPath = path;

    /*
      Exception: `sortby=relevance` is auto-applied client-side by
      useSetUpRelevanceAsInitialSort as the category's default sort, so a bare path
      immediately gets this query param pushed onto it again. Stripping it here made the
      canonical tag point back to a URL the page redirects away from, creating a
      canonical/redirect loop that kept search engines from indexing either URL.
    */
    const sortby = queryString ? new URLSearchParams(queryString).get('sortby') : null;
    if (sortby === 'relevance') {
      sanitizedPath = `${path}?sortby=relevance`;
    }
  }
  return { sanitizedPath };
}

export default function CanonicalTag(): JSX.Element | null {
  const router = useRouter();
  const UI = useUI();
  const { dispensary } = useDispensary();
  const requestHost = useRequestHost();

  const getCanonicalUrl = (): string => {
    const hasCustomDomain = !!dispensary?.customDomainSettings?.domain;
    const { sanitizedPath } = sanitizePathForSeo(router ?? {});

    /*
      If viewing storeFront variant (regardless of domain it's served from), prefer dispensary's custom
      domain as canonical, if it exists. In all other cases (dutchie.com, no custom domain exists, etc.),
      default to marketplace URL from env.
    */
    if (UI.isVariant(`store-front`) && hasCustomDomain) {
      const customDomain: string = dispensary.customDomainSettings.domain;
      // requestHost is threaded from server.js (via _app's getInitialProps) so it's available
      // during SSR, not just after hydration -- window.location.host is undefined server-side,
      // which previously meant the *initial* HTML always fell back to customDomain regardless of
      // which host actually served the page. requestHost covers that; window.location.host is
      // kept only as a last-resort fallback (e.g. tests/storybook rendering without RequestHostProvider).
      const currentHost = requestHost ?? (typeof window !== `undefined` ? window.location.host : ``);

      /*
        customDomainSettings.domain is a single stored value (e.g. "example.com"), but a
        customer's DNS/CDN can serve the same store on both the bare domain and a www.
        subdomain. Prefer whichever host the page is actually being served on when it's
        the same domain (with or without www.) as what's stored, so the canonical tag
        matches the served URL instead of always pointing at the stored variant and
        splitting SEO signal between the two hosts.
      */
      const normalizeDomain = (host: string): string => host.replace(/^www\./, ``);
      const canonicalHost =
        currentHost && normalizeDomain(currentHost) === normalizeDomain(customDomain) ? currentHost : customDomain;

      return `https://${canonicalHost}${sanitizedPath}`;
    }

    // For SEO, we want /stores, /embedded-menu, and /kiosks to link back to their /dispensary equivalents
    if (UI.isVariant(`store-front`) || UI.isVariant(`embedded`) || UI.isVariant(`kiosk`)) {
      return `${PublicEnv.consumerUrl}${getDispensaryPathEquivalent(sanitizedPath)}`;
    }

    // All other cases, we self-reference
    return `${PublicEnv.consumerUrl}${sanitizedPath}`;
  };

  return (
    <Head>
      <link rel='canonical' href={getCanonicalUrl()} />
    </Head>
  );
}
